import { designTokens } from '@/utils/design-tokens'
import type { StrategySummaryMonthlyItem } from '@/api/generated/models/StrategySummaryMonthlyItem'
import {
  getHighlightMonth,
  getMonthlyDoneCounts,
  getMonthlyRate,
  formatNumber,
  getRateColor,
  strategyColors,
  type MetricKey,
} from '../strategyMetrics'

interface MonthlyBarsProps {
  monthly: StrategySummaryMonthlyItem[]
  metricKey: MetricKey
  /** 누른 달 (YYYY-MM). 지난 달·이번 달만 고를 수 있다 */
  selectedMonth: string
  onSelectMonth: (month: string) => void
}

const HEIGHT = 132
const MAX_BAR = 84

/** 학년도 12칸 월별 누적 막대. 지난 달은 누적값, 고른 달 강조색, 남은 달은 빈 칸 */
export default function MonthlyBars({
  monthly,
  metricKey,
  selectedMonth,
  onSelectMonth,
}: MonthlyBarsProps) {
  const font = designTokens.typography.fontFamily.sans[0]
  const hasValue = monthly.some(
    (item) => getMonthlyRate(item, metricKey) != null
  )

  if (!hasValue) {
    return (
      <div
        style={{
          height: HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: font,
          fontSize: 13,
          color: strategyColors.mutedText,
          border: `1px dashed ${strategyColors.dashed}`,
          borderRadius: 8,
        }}
      >
        월별 현황 없음
      </div>
    )
  }

  const lastElapsedKey = getHighlightMonth(monthly)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 4,
        height: HEIGHT,
      }}
    >
      {monthly.map((item) => {
        const [year, month] = item.month.split('-').map(Number)
        const rate = getMonthlyRate(item, metricKey)
        const isFuture = item.month > lastElapsedKey
        const isSelected = item.month === selectedMonth
        // 세부실행과제·업무 진행률은 그 달 완료 건수도 함께 보여준다 (마우스를 올렸을 때)
        const doneInMonth = getMonthlyDoneCounts(item, metricKey)?.inMonth
        const doneText =
          doneInMonth != null ? ` · 완료 ${formatNumber(doneInMonth)}건` : ''
        const title = isFuture
          ? `${year}년 ${month}월 (예정)`
          : `${year}년 ${month}월${doneText} · 누적 ${rate == null ? '—' : `${rate.toFixed(1)}%`}`

        return (
          <button
            key={item.month}
            type="button"
            title={title}
            aria-label={title}
            aria-pressed={isSelected}
            disabled={isFuture}
            onClick={() => onSelectMonth(item.month)}
            style={{
              flex: '1 1 0',
              minWidth: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 3,
              padding: 0,
              border: 0,
              borderRadius: 4,
              background: isSelected ? strategyColors.naSurface : 'transparent',
              cursor: isFuture ? 'default' : 'pointer',
              fontFamily: font,
            }}
          >
            <span
              style={{
                fontSize: isSelected ? 12 : 10,
                fontWeight: isSelected ? 700 : 400,
                color: isSelected
                  ? strategyColors.text
                  : strategyColors.subText,
              }}
            >
              {isFuture || rate == null ? '' : Math.round(rate)}
            </span>
            <div
              style={{
                width: '100%',
                maxWidth: 24,
                height:
                  isFuture || rate == null
                    ? 2
                    : Math.max(
                        2,
                        Math.round((Math.min(rate, 100) * MAX_BAR) / 100)
                      ),
                borderRadius: isFuture ? 1 : '4px 4px 0 0',
                background:
                  isFuture || rate == null
                    ? strategyColors.track
                    : isSelected
                      ? getRateColor(rate)
                      : strategyColors.monthPast,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: isSelected ? 700 : 400,
                color: isSelected
                  ? strategyColors.text
                  : strategyColors.subText,
                whiteSpace: 'nowrap',
              }}
            >
              {month}월
            </span>
          </button>
        )
      })}
    </div>
  )
}
